import { useSyncExternalStore } from 'react'

/**
 * The seat map's pan/zoom, shared with the one component that follows it
 * frame by frame (the minimap) WITHOUT going through the page's state.
 *
 * The storefront page used to keep the map transform in its own useState, so
 * every frame of a pan or pinch re-rendered the whole select-seats page
 * (sidebar, price key, Best Available, every formatted price) just to move the
 * minimap's viewport box. A store lets the minimap subscribe on its own.
 */
export interface MapTransform {
  x: number
  y: number
  scale: number
}

export interface MapTransformStore {
  get: () => MapTransform
  /** Stable — safe to pass straight to UnifiedSeatMap's onTransformChange. */
  set: (t: MapTransform) => void
  subscribe: (listener: () => void) => () => void
}

export function createMapTransformStore(initial: MapTransform = { x: 0, y: 0, scale: 1 }): MapTransformStore {
  let current = initial
  const listeners = new Set<() => void>()
  return {
    get: () => current,
    set: (t) => {
      if (t === current) return
      current = t
      listeners.forEach(l => l())
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
  }
}

/** The latest transform; re-renders the caller (only) when it changes. */
export function useMapTransform(store: MapTransformStore): MapTransform {
  return useSyncExternalStore(store.subscribe, store.get, store.get)
}
